import React from 'react';
import { MenuItemProps } from '@mui/material';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import { SelectItem } from '@askporter/utils';
import { BaseAutocomplete } from '../RevampedAutocomplete/BaseAutocomplete';
import type { BaseAutocompleteValue } from '../RevampedAutocomplete/types';

type SelectItemType = SelectItem<MenuItemProps['value']>;

export type SelectItemAutocompleteProps = {
  t: (key: string, options?: Record<string, string | number>) => string;
  idPrefix: string;
  options: SelectItemType[];
  value: SelectItemType | null;
  onChange: (value: SelectItemType | null) => void;
  error?: boolean;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  inputValue?: string;
  onInputChange?: (event: React.SyntheticEvent, newInputValue: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  ref?: React.Ref<HTMLDivElement>;
  freeSolo?: boolean;
  filterOptions?: (options: SelectItemType[], state: { inputValue: string }) => SelectItemType[];
  isOptionEqualToValue?: (option: SelectItemType, value: SelectItemType) => boolean;
  getOptionLabel?: (option: SelectItemType) => string;
  groupBy?: (option: SelectItemType) => string;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: SelectItemType,
    state: { selected: boolean; index: number },
  ) => React.ReactNode;
};

export const SelectItemAutocomplete: React.FC<SelectItemAutocompleteProps> = ({
  t,
  idPrefix,
  options,
  value,
  onChange,
  error,
  label,
  placeholder,
  disabled,
  isLoading,
  inputValue,
  onInputChange,
  onBlur,
  ref,
  freeSolo = false,
  filterOptions,
  isOptionEqualToValue,
  getOptionLabel,
  groupBy,
  renderOption,
}) => {
  const toBaseValue = (option: SelectItemType) => ({
    uid: option.value as string,
    // Ensure label is always a string to satisfy BaseAutocompleteValue typing
    label: typeof option.label === 'string' ? option.label : '',
    avatar: undefined as string | undefined,
    iconPath: option.iconPath,
  });
  const fromBaseValue = (uid: string) => options.find((opt) => opt.value === uid);
  const mapOptionToValue = (option: unknown) => {
    if (option && typeof option === 'object' && 'uid' in option) {
      const { uid, label, iconPath } = option as {
        uid: string;
        label?: unknown;
        iconPath?: string;
      };
      return {
        uid,
        label: typeof label === 'string' ? label : '',
        iconPath: iconPath,
      };
    }
    return toBaseValue(option as SelectItemType);
  };

  // Convert data
  const baseAutocompleteOptions = options.map(toBaseValue);
  const baseAutocompleteValue = value ? toBaseValue(value) : null;

  // Event handlers
  const handleChange = (newValue: unknown) => {
    if (!newValue) {
      onChange(null);
      return;
    }
    const originalOption = fromBaseValue((newValue as { uid: string }).uid);
    onChange(originalOption || null);
  };

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    onInputChange?.(event, newInputValue);
  };

  // Filter function
  const customFilterOptions = (
    baseOptions: BaseAutocompleteValue[],
    { inputValue }: FilterOptionsState<BaseAutocompleteValue>,
  ): BaseAutocompleteValue[] => {
    if (freeSolo) return baseOptions;

    if (filterOptions) {
      const originalOptions = baseOptions.map((opt) => ({
        value: opt.uid,
        label: opt.label ?? '',
        iconPath: opt.iconPath,
      })) as SelectItemType[];
      const filtered = filterOptions(originalOptions, { inputValue });
      return filtered.map(toBaseValue);
    }

    if (!inputValue) return baseOptions;
    return baseOptions.filter((option) => (option.label ?? '').toLowerCase().includes(inputValue.toLowerCase()));
  };

  // Convert renderOption to work with BaseAutocompleteValue
  const convertedRenderOption = renderOption
    ? (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: BaseAutocompleteValue,
        state: { selected: boolean; index: number },
      ) => {
        const originalOption = fromBaseValue(option.uid);
        return originalOption ? renderOption(props, originalOption, state) : null;
      }
    : undefined;

  return (
    <BaseAutocomplete
      t={t}
      idPrefix={idPrefix}
      options={baseAutocompleteOptions}
      value={baseAutocompleteValue}
      onChange={handleChange}
      mapOptionToValue={mapOptionToValue}
      error={error}
      label={label}
      placeholder={placeholder ?? ''}
      disabled={disabled}
      loading={isLoading}
      isListAvatarVisible={false}
      {...(inputValue !== undefined && { inputValue })}
      onInputChange={handleInputChange}
      onBlur={onBlur}
      ref={ref}
      freeSolo={freeSolo}
      filterOptions={customFilterOptions}
      {...(renderOption && { renderOption: convertedRenderOption })}
      {...(groupBy && {
        groupBy: (option: BaseAutocompleteValue) => {
          const originalOption = fromBaseValue(option.uid);
          return originalOption ? groupBy(originalOption) : '';
        },
      })}
      {...(isOptionEqualToValue && {
        isOptionEqualToValue: (option: BaseAutocompleteValue, val: BaseAutocompleteValue) => {
          const originalOption = fromBaseValue(option.uid);
          const originalValue = fromBaseValue(val.uid);
          return originalOption && originalValue ? isOptionEqualToValue(originalOption, originalValue) : false;
        },
      })}
      {...(getOptionLabel && {
        getOptionLabel: (option: BaseAutocompleteValue) => {
          const originalOption = fromBaseValue(option.uid);
          return originalOption ? getOptionLabel(originalOption) : '';
        },
      })}
    />
  );
};

SelectItemAutocomplete.displayName = 'SelectItemAutocomplete';
